import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { FiRefreshCw, FiLogOut, FiMenu, FiWifi, FiWifiOff, FiMoon, FiSun, FiGlobe, FiCheck } from 'react-icons/fi';
import { syncApi } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useOperations } from '../../contexts/OperationsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import NotificationCenter from '../common/NotificationCenter';

const LANGUAGES = [
  { code: 'pl', label: 'Polski', short: 'PL' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
];

const LanguagePicker: React.FC<{ current: string; onChange: (lng: string) => void }> = ({ current, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          color: 'var(--text)',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
      >
        <FiGlobe size={13} style={{ opacity: 0.6 }} />
        {currentLang.short}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 9999,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            padding: 4,
            minWidth: 140,
          }}
        >
          {LANGUAGES.map((lang) => {
            const selected = lang.code === current;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => { onChange(lang.code); setOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: 7,
                  background: selected ? 'var(--red-light, rgba(239,68,68,0.1))' : 'transparent',
                  color: selected ? 'var(--red, #ef4444)' : 'var(--text)',
                  fontWeight: selected ? 600 : 450,
                  fontSize: 13,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'var(--gray-100, rgba(255,255,255,0.05))'; }}
                onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 11, opacity: 0.5 }}>{lang.short}</span>
                  {lang.label}
                </span>
                {selected && <FiCheck size={14} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface HeaderProps {
  onMenuToggle: () => void;
  signalRConnected?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle, signalRConnected = true }) => {
  const { instance, accounts } = useMsal();
  const { hasWrite } = usePermissions();
  const { startOperation, updateProgress, completeOperation, failOperation } = useOperations();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();

  const [currentLang, setCurrentLang] = useState(i18n.language || 'pl');
  const changeLanguage = useCallback((lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('app-language', lng);
    setCurrentLang(lng);
  }, [i18n]);
  const [syncing, setSyncing] = useState(false);

  const account = accounts[0];
  const displayName = account?.name || account?.username || 'Uzytkownik';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    const opId = startOperation({
      type: 'sync',
      label: 'Synchronizacja z Amazon',
      progress: { current: 0, total: 4 },
    });

    try {
      updateProgress(opId, 1, 'Synchronizacja zamówień...');
      await syncApi.syncAll();
      updateProgress(opId, 2, 'Synchronizacja produktów...');

      // Simulated steps since syncAll is one call
      setTimeout(() => updateProgress(opId, 3, 'Synchronizacja stanów magazynowych...'), 1000);
      setTimeout(() => {
        updateProgress(opId, 4, 'Zakończono');
        completeOperation(opId, { success: 4, failed: 0, errors: [] });
      }, 2000);

      toast.success('Synchronizacja rozpoczęta');
    } catch (error: any) {
      failOperation(opId, error.response?.data?.message || 'Błąd synchronizacji');
      toast.error(error.response?.data?.message || 'Błąd synchronizacji');
    } finally {
      setSyncing(false);
    }
  }, [startOperation, updateProgress, completeOperation, failOperation]);

  const handleLogout = useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);

  return (
    <header className="top-header">
      <div className="header-left">
        <button className="hamburger-btn" onClick={onMenuToggle} aria-label="Menu">
          <FiMenu size={20} />
        </button>
        <span className="header-title">Amazon Vendor & Seller Hub</span>
      </div>

      <div className="header-right">
        {/* Connection status */}
        <div
          className={`connection-indicator ${signalRConnected ? 'connected' : 'disconnected'}`}
          title={signalRConnected ? 'Połączono z serwerem' : 'Brak połączenia z serwerem'}
        >
          {signalRConnected ? <FiWifi size={12} /> : <FiWifiOff size={12} />}
        </div>

        {hasWrite('Sync' as any) && (
          <button
            className="header-sync-btn"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} size={14} />
            <span className="header-sync-label">
              {syncing ? t('header.syncing') : t('header.sync')}
            </span>
          </button>
        )}

        {/* Language selector */}
        <LanguagePicker current={currentLang} onChange={changeLanguage} />

        <button
          className="btn-icon"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Tryb jasny' : 'Tryb ciemny'}
          style={{ marginRight: '4px' }}
        >
          {theme === 'dark' ? <FiSun size={16} /> : <FiMoon size={16} />}
        </button>

        <NotificationCenter />

        <div className="header-user">
          <div className="header-avatar">{initials}</div>
          <span className="header-user-name">{displayName}</span>
        </div>

        <button className="btn-icon" onClick={handleLogout} title="Wyloguj">
          <FiLogOut size={16} />
        </button>
      </div>
    </header>
  );
};

export default Header;
